import { Badge, Box, Button, Card, Flex, Heading, Text } from '@nrx/ui';
import type { JSX } from 'react';

import type { GroupConsultDayBucket, GroupConsultEvent } from './group-consult-calendar.js';
import {
  formatEventTimeRangeCompact,
  formatGroupConsultDayHeading,
} from './group-consult-calendar.js';

type GroupConsultDayPanelProps = {
  selectedDay: GroupConsultDayBucket | null;
  timeZone: string;
  showRegisterButton: boolean;
  registerLoading: boolean;
  onRegisterClick: (event: GroupConsultEvent) => void;
  onClose?: () => void;
};

/**
 * Show the selected day's sessions beneath the month grid.
 *
 * @remarks The session count is a `Badge` and each session a `Card elevation="flat"`. The session
 * title stays a `Heading` inside the row rather than `Card title`, which would lift it above the
 * row and break the title/Register alignment the vendor layout had.
 *
 * Registration is `Button busy`, so the in-flight state carries `aria-busy` rather than a bare
 * `disabled` (W4); the spec asserts both the "Registering..." label and that the control is
 * disabled, and `busy` sets the latter itself.
 */
export function GroupConsultDayPanel({
  selectedDay,
  timeZone,
  showRegisterButton,
  registerLoading,
  onRegisterClick,
  onClose,
}: GroupConsultDayPanelProps): JSX.Element {
  if (!selectedDay) {
    return (
      <Card elevation="flat" className="consult-day-panel" aria-live="polite">
        <Heading as="h4" size="4">
          Daily schedule
        </Heading>
        <Text as="p" size="2" color="muted" className="consult-day-panel__hint">
          Select a highlighted day to see the available group consults.
        </Text>
      </Card>
    );
  }

  return (
    <Card elevation="soft" className="consult-day-panel" aria-live="polite">
      <Flex align="start" justify="between" gap="4" className="consult-day-panel__header">
        <Box>
          <Heading as="h4" size="4">
            Available sessions
          </Heading>
          <Text as="p" size="2" color="muted" className="consult-day-panel__date">
            {formatGroupConsultDayHeading(selectedDay.date)}
          </Text>
        </Box>
        <Flex align="center" gap="2">
          <Badge variant="accent">
            {selectedDay.events.length} session{selectedDay.events.length === 1 ? '' : 's'}
          </Badge>
          {onClose ? (
            <Button tone="secondary" onClick={onClose} className="consult-day-panel__close">
              Close
            </Button>
          ) : null}
        </Flex>
      </Flex>

      <Flex direction="column" gap="3" className="consult-day-panel__sessions">
        {selectedDay.events.map((event) => (
          <Card key={event.id} elevation="flat" className="consult-day-session">
            <Flex align="start" justify="between" gap="3" className="consult-day-session__row">
              <Box className="consult-day-session__detail">
                <Heading as="h5" size="3">
                  {event.title}
                </Heading>
                {event.description ? (
                  <Text as="p" size="2" color="muted" className="consult-preserve-lines">
                    {event.description}
                  </Text>
                ) : null}
                <Text as="p" size="2" className="consult-day-session__when">
                  {formatEventTimeRangeCompact(event.start, event.end, timeZone)}
                </Text>
                {event.limitedAvailability ? (
                  <Badge variant="warning" className="consult-day-session__badge">
                    Limited availability
                  </Badge>
                ) : null}
              </Box>

              {showRegisterButton ? (
                <Button
                  busy={registerLoading}
                  onClick={() => onRegisterClick(event)}
                  className="consult-register-button"
                >
                  {registerLoading ? 'Registering...' : 'Register'}
                </Button>
              ) : null}
            </Flex>
          </Card>
        ))}
      </Flex>
    </Card>
  );
}
